import { useState } from "react"
import { NavLink } from "react-router-dom"
import { Helmet } from "react-helmet"
import { useSelector } from "react-redux"
import type { RootState } from "../app/store"
import type { Project } from "../lib/types"
import Modal from "../components/Modal"
import Footer from "../components/Footer/Footer"
import ScrollReveal from "../components/ScrollReveal"
import SkeletonImage from "../components/SkeletonImage"
import { FaArrowRight } from "react-icons/fa"

const workProcess = [
  { step: "01", title: "Discovery", desc: "Understanding your goals, audience, and requirements." },
  { step: "02", title: "Planning", desc: "Structuring the architecture and defining the roadmap." },
  { step: "03", title: "Design", desc: "Crafting clean UI/UX with attention to every detail." },
  { step: "04", title: "Development", desc: "Building scalable, performant, and maintainable code." },
  { step: "05", title: "Launch", desc: "Deploying and handing off a production-ready product." },
]

const services = [
  {
    category: "Frontend",
    items: ["React & Next.js", "Vue.js & Angular", "Responsive UI/UX", "Tailwind & CSS Modules"],
  },
  {
    category: "Backend",
    items: ["Laravel & Django", "Node.js & Express", "REST APIs & Auth", "Database Design"],
  },
  {
    category: "Full Stack",
    items: ["End-to-end apps", "Security & Performance", "Third-party integrations", "CMS & E-Commerce"],
  },
]

export function NewHome() {
  const projects = useSelector((state: RootState) => state.projects.items)
  const [visible, setVisible] = useState(false)
  const [modalData, setModalData] = useState<Project | null>(null)

  if (visible) {
    document.body.style.overflow = "hidden"
  } else {
    document.body.style.overflow = "unset"
  }

  const handleClick = (project: Project) => {
    setModalData(project)
    setVisible(true)
  }
  const handleClose = () => {
    setVisible(false)
  }
  const handleNext = () => {
    if (!modalData) return
    const currentIndex = projects.findIndex((p) => p.id === modalData.id)
    const nextIndex = (currentIndex + 1) % projects.length
    setModalData(projects[nextIndex])
  }
  const handlePrev = () => {
    if (!modalData) return
    const currentIndex = projects.findIndex((p) => p.id === modalData.id)
    const prevIndex = (currentIndex - 1 + projects.length) % projects.length
    setModalData(projects[prevIndex])
  }

  return (
    <>
      <Helmet>
        <title>Muhammed Mashady | Full Stack Web Developer</title>
        <meta
          name="description"
          content="I'm Muhammed Mashady, a full stack web developer skilled in Laravel, React, Django, and Vue. Explore my portfolio and projects."
        />
        <meta
          name="keywords"
          content="Full Stack Developer, Laravel, React, Vue, Django, Freelance Developer, Web Portfolio"
        />
        <meta name="author" content="Muhammed Mashady" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mashady.vercel.app/" />
        <meta property="og:title" content="Muhammed Mashady | Full Stack Web Developer" />
        <meta property="og:description" content="Muhammed Mashady - Full Stack Web Developer. Crafting high-quality digital experiences with React, Laravel, and Django." />
        <meta property="og:image" content="https://mashady.vercel.app/fav.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://mashady.vercel.app/" />
        <meta name="twitter:title" content="Muhammed Mashady | Full Stack Web Developer" />
        <meta name="twitter:description" content="Muhammed Mashady - Full Stack Web Developer. Crafting high-quality digital experiences with React, Laravel, and Django." />
        <meta name="twitter:image" content="https://mashady.vercel.app/fav.png" />
      </Helmet>

      {/* ── Hero ── */}
      <section className="max-w-[1240px] mx-auto px-6 pt-20 pb-20 md:pt-32 md:pb-28">
        <ScrollReveal delay={0.05}>
          <span
            className="inline-block text-sm px-3 py-1 rounded-full mb-8 ubuntu-medium"
            style={{ backgroundColor: "var(--secondary-color)", color: "var(--main-white)" }}
          >
            Full Stack Developer
          </span>
        </ScrollReveal>
        <ScrollReveal delay={0.15}>
          <h1
            className="text-[64px] md:text-[100px] lg:text-[120px] ubuntu-bold leading-[1] mb-6"
            style={{ color: "var(--main-white)" }}
          >
            Build digital.
          </h1>
        </ScrollReveal>
        <ScrollReveal delay={0.25}>
          <p
            className="text-[18px] md:text-[22px] ubuntu-regular max-w-[580px] mb-10"
            style={{ color: "var(--typo)" }}
          >
            Full stack web development for startups and tech teams — from idea to
            production-ready product.
          </p>
        </ScrollReveal>
        <ScrollReveal delay={0.35}>
          <NavLink
            to="/projects"
            className="btn inline-flex items-center gap-2 text-[17px]"
          >
            See projects <FaArrowRight className="text-[14px]" />
          </NavLink>
        </ScrollReveal>
      </section>

      {/* ── Projects grid ── */}
      <section className="max-w-[1240px] mx-auto px-6 py-16">
        <ScrollReveal>
          <h2
            className="text-center text-[28px] md:text-[36px] ubuntu-bold mb-10"
            style={{ color: "var(--main-white)" }}
          >
            ( Our projects )
          </h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.slice(0, 6).map((project, i) => (
            <ScrollReveal key={project.id} delay={i * 0.08}>
              <div
                className="group relative overflow-hidden rounded-2xl cursor-pointer"
                style={{ aspectRatio: "4/3" }}
                onClick={() => handleClick(project)}
              >
                <SkeletonImage
                  src={project.cover}
                  alt={project.name}
                  className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0 flex items-end p-5 opacity-0 group-hover:opacity-100 transition duration-300"
                  style={{ background: "linear-gradient(to top, rgba(26,23,27,0.95) 0%, transparent 60%)" }}
                >
                  <div>
                    <span
                      className="text-xs uppercase tracking-widest ubuntu-medium"
                      style={{ color: "var(--secondary-color)" }}
                    >
                      {project.projectType}
                    </span>
                    <h3
                      className="text-[17px] ubuntu-bold"
                      style={{ color: "var(--main-white)" }}
                    >
                      {project.name}
                    </h3>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={0.1}>
          <div className="flex justify-end mt-6">
            <NavLink
              to="/projects"
              className="inline-flex items-center gap-2 text-[16px] hover:underline ubuntu-medium"
              style={{ color: "var(--secondary-color)" }}
            >
              View all projects <FaArrowRight className="text-[13px]" />
            </NavLink>
          </div>
        </ScrollReveal>
      </section>

      {/* ── Work process ── */}
      <section style={{ backgroundColor: "var(--simi-dark-color)" }} className="py-20">
        <div className="max-w-[1240px] mx-auto px-6">
          <ScrollReveal>
            <h2
              className="text-center text-[28px] md:text-[36px] ubuntu-bold mb-14"
              style={{ color: "var(--main-white)" }}
            >
              ( Work process )
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-8">
            {workProcess.map((item, i) => (
              <ScrollReveal key={item.step} delay={i * 0.1} direction="up">
                <div className="text-center">
                  <div
                    className="text-[52px] ubuntu-bold leading-none mb-3"
                    style={{ color: "var(--secondary-color)", opacity: 0.35 }}
                  >
                    {item.step}
                  </div>
                  <h3
                    className="text-[17px] ubuntu-bold mb-2"
                    style={{ color: "var(--main-white)" }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-[14px]" style={{ color: "var(--typo)" }}>
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="max-w-[1240px] mx-auto px-6 py-20">
        <ScrollReveal>
          <h2
            className="text-center text-[28px] md:text-[36px] ubuntu-bold mb-14"
            style={{ color: "var(--main-white)" }}
          >
            ( What I build )
          </h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {services.map((service, i) => (
            <ScrollReveal key={service.category} delay={i * 0.12}>
              <div>
                <h3
                  className="text-[20px] ubuntu-bold mb-5"
                  style={{ color: "var(--secondary-color)" }}
                >
                  {service.category}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item) => (
                    <li
                      key={item}
                      className="pb-3 text-[15px] ubuntu-regular"
                      style={{
                        color: "var(--typo)",
                        borderBottom: "1px solid #2e2a30",
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section style={{ backgroundColor: "var(--secondary-color)" }} className="py-24">
        <div className="max-w-[1240px] mx-auto px-6 text-center">
          <ScrollReveal>
            <h2
              className="text-[36px] md:text-[60px] ubuntu-bold mb-8 leading-tight"
              style={{ color: "var(--main-white)" }}
            >
              Let's discuss your project!
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <NavLink
              to="/contact"
              className="inline-block ubuntu-bold text-[17px] px-8 py-3 rounded-lg transition duration-300"
              style={{
                backgroundColor: "var(--main-white)",
                color: "var(--secondary-color)",
              }}
            >
              Get in touch
            </NavLink>
          </ScrollReveal>
        </div>
      </section>

      {/* ── About blurb ── */}
      <section
        style={{ backgroundColor: "var(--simi-dark-color)" }}
        className="py-20"
      >
        <div className="max-w-[860px] mx-auto px-6 text-center">
          <ScrollReveal>
            <p
              className="text-[20px] md:text-[26px] ubuntu-medium leading-relaxed"
              style={{ color: "var(--main-white)" }}
            >
              I handle both frontend and backend so your team can stay focused on
              what matters — I build the entire product, from the first line of code to
              a live, production-ready release.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <NavLink
              to="/about"
              className="inline-flex items-center gap-2 mt-8 ubuntu-medium text-[16px] hover:underline"
              style={{ color: "var(--secondary-color)" }}
            >
              More about me <FaArrowRight className="text-[13px]" />
            </NavLink>
          </ScrollReveal>
        </div>
      </section>

      <Modal
        visible={visible}
        modalData={modalData}
        handleClose={handleClose}
        onNext={handleNext}
        onPrev={handlePrev}
      />
      <Footer />
    </>
  )
}
